import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import MoleculeAuthInput from '../auth/molecules/MoleculeAuthInput';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useGroupMember } from '../../shared/hooks/useApi/useGroupMember';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useGroupMemberLinkStore } from '../../shared/store/groupMemberLinkStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { useValidation } from '../../shared/hooks/useValidation';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType, ModalType } from '../../shared/enum/EnumRepository';
import deleteIcon from '../../asset/icon/delete.png';

interface GroupMemberLinkModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const GroupMemberLinkModal: React.FC<GroupMemberLinkModalProps> = ({ setIsUserInput }) => {
  const { standardModal, setStandardModal, clearModal } = useModalStore();
  const member = useGroupMemberStore((state) =>
    state.groupMemberList.find((item) => item.id === standardModal?.value),
  );
  const { previewGroupMemberLink } = useGroupMember();
  const { setOverwriteData, clearOverwriteData } = useGroupMemberLinkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isLoading } = useLoadingStore();
  const { isEmail } = useValidation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsUserInput(email.length > 0 || password.length > 0);
  }, [email, password, setIsUserInput]);

  useEffect(() => {
    clearOverwriteData();
    const inputElement = document.querySelector('input') as HTMLInputElement;
    inputElement?.focus();
  }, [clearOverwriteData]);

  if (!member || !standardModal) return null;

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  };

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError('');
      return true;
    }
    setPasswordError('');
    return true;
  };

  const changePasswordInput = (value: string) => {
    validatePassword(value);
    setPassword(value);
  };

  const handleConnect = async () => {
    if (email === '' || password === '') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }
    if (emailError || passwordError) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }

    const result = await previewGroupMemberLink({
      id: member.id,
      email,
      password,
    });
    if (!result) return;

    setOverwriteData(result);
    setStandardModal({
      modalType: ModalType.GROUP_MEMBER_LINK_CONFIRM,
      title: null,
      value: member.id,
      handleChange: () => {},
    });
  };

  const handleClose = () => {
    clearOverwriteData();
    clearModal();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) return;
    switch (event.key) {
      case 'Enter':
        if (event.target instanceof HTMLButtonElement) return;
        handleConnect();
        break;
      case 'Escape':
        handleClose();
        break;
      default:
        break;
    }
  };

  return (
    <LinkComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <HeaderRow>
        <Title>{getModalMessage('GROUP_MEMBER_LINK_TITLE')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={handleClose}
        />
      </HeaderRow>
      <MoleculeAuthInput
        name={getModalMessage('AUTH_EMAIL_LABEL')}
        value={email}
        handleChange={changeEmailInput}
        err={emailError}
        placeholder={getModalMessage('AUTH_EMAIL_LABEL')}
        nameVisible={false}
        errVisible={false}
      />
      <MoleculeAuthInput
        name={getModalMessage('AUTH_PASSWORD_LABEL')}
        value={password}
        handleChange={changePasswordInput}
        err={passwordError}
        placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
        nameVisible={false}
        errVisible={false}
      />
      <SubmitButton onClick={handleConnect} disabled={isLoading > 0}>
        {getBtnName('CONNECT_ACCOUNT')}
      </SubmitButton>
    </LinkComp>
  );
};

export default GroupMemberLinkModal;

const LinkComp = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  padding: 17px 14px;
  outline: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3vh;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;
